// AllocationKeys.jsx
import React, { useState } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Table, 
    TableBody, 
    TableHead, 
    TableRow, 
    CircularProgress, 
    Paper 
} from "@mui/material";
import { RefreshCw } from 'lucide-react';
import { StyledContainer, StyledCard, StyledTableContainer, StyledTableRow, StyledTableCell, StyledTableHeadCell } from "../../../styles/purchaseInvoices/pages/commonTableStyles";
import { useResponsive } from '../../../hooks/purchaseInvoices/useResponsive.js';
import useAllocationKeys from '../../../hooks/purchaseInvoices/useAllocationKeys';
import { styles } from '../../../styles/purchaseInvoices/pages/allocationKeysStyles';

const AllocationKeys = () => {
  const { isDesktop } = useResponsive();
  const [refreshing, setRefreshing] = useState(false);
  
  const {
    allocationKeys,
    isLoading,
    error,
    lastUpdated,
    refreshPriorityKeys,
    formatDate
  } = useAllocationKeys();
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshPriorityKeys();
    setRefreshing(false);
  };
  
  const columns = [
    {
      accessorKey: 'name',
      header: 'תיאור מפתח העמסה',
    }
  ];
  
  const renderMobileCard = (key, index) => {
    return (
      <Paper key={key.id || index} elevation={1} sx={styles.mobileCard}>
        <Box sx={styles.mobileCardHeader}>
          <Typography variant="subtitle1" fontWeight="bold">
            {key.name}
          </Typography>
        </Box>
      </Paper>
    );
  };

  if (isLoading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
        <Typography variant="h6" color="textSecondary" sx={styles.loadingText}>
          טוען נתונים...
        </Typography>
      </Box>
    );
  }

  return (
    <StyledContainer>
      <StyledCard>
        <Box sx={styles.headerBox}>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<RefreshCw size={20} />}
            sx={styles.addButtonResponsive}
          >
            {refreshing ? "מרענן..." : "רענן מפתחות מפריוריטי"}
          </Button>
        </Box>

        {error ? (
          <Box sx={styles.errorBox}>
            <Typography variant="h6" color="error" sx={styles.errorText}>
              שגיאה בטעינת מפתחות העמסה
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRefresh}
            >
              נסה שנית
            </Button>
          </Box>
        ) : isDesktop ? (
          <StyledTableContainer sx={styles.tableMinHeight}>
            <Table dir="rtl">
              <TableHead>
                <TableRow>
                  {columns.map(column => (
                    <StyledTableHeadCell 
                      key={column.accessorKey} 
                      align="left"
                    >
                      {column.header}
                    </StyledTableHeadCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {allocationKeys.length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={columns.length} align="center">
                      <Typography variant="body1" color="text.secondary">
                        אין מפתחות העמסה להצגה
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  allocationKeys.map((row, index) => (
                    <StyledTableRow key={row.id || index}>
                      {columns.map((column, columnIndex) => (
                        <StyledTableCell 
                          key={columnIndex} 
                          align="left"
                        >
                          {row[column.accessorKey] || '-'}
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        ) : (
          <Box sx={styles.mobileCardContainer}>
            {allocationKeys.length === 0 ? (
              <Box sx={styles.emptyBox}>
                <Typography variant="body1" color="text.secondary">
                  אין מפתחות העמסה להצגה
                </Typography>
              </Box>
            ) : (
              allocationKeys.map((key, index) => renderMobileCard(key, index))
            )}
          </Box>
        )}

        <Box sx={styles.footerBox}>
          <Box sx={styles.footerContent}>
            {lastUpdated && (
              <Typography variant="body2" sx={styles.lastUpdatedText}>
                <RefreshCw size={14} />
                עדכון אחרון: {formatDate(lastUpdated)}
              </Typography>
            )}
            
            <Typography variant="body2" color="text.secondary">
              {allocationKeys.length > 0 
                ? `סה"כ ${allocationKeys.length} מפתחות העמסה`
                : ''
              }
            </Typography>
          </Box>
        </Box>
      </StyledCard>
    </StyledContainer>
  );
};

export default AllocationKeys;